import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Coins, Zap, Crown, Building2, Wallet, Check, Copy, QrCode, Bitcoin, Loader2, ExternalLink, AlertCircle, Clock, ArrowRight, CheckCircle, XCircle, Shield, CreditCard, FileText, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const PACKAGE_ICONS = [Coins, Zap, Crown, Building2];
const PACKAGE_COLORS = [
  'border-blue-200 bg-blue-50 hover:border-blue-300',
  'border-purple-200 bg-purple-50 hover:border-purple-300',
  'border-amber-200 bg-amber-50 hover:border-amber-300',
  'border-emerald-200 bg-emerald-50 hover:border-emerald-300',
];

export default function CreditsPage() {
  const { user, checkAuth } = useAuth();
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [oasisBalance, setOasisBalance] = useState(null);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [step, setStep] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [pixCopied, setPixCopied] = useState(false);
  const [creditRequest, setCreditRequest] = useState(null);
  const [checking, setChecking] = useState(false);

  const [boletoForm, setBoletoForm] = useState({
    first_name: '', last_name: '', cpf: '', zip_code: '',
    street: '', number: '', neighborhood: '', city: '', state: ''
  });

  const [cardForm, setCardForm] = useState({
    number: '', name: '', expMonth: '', expYear: '', cvv: '', cpf: ''
  });
  const [mpReady, setMpReady] = useState(false);
  const mpRef = useRef(null);

  const pollingRef = useRef(null);

  // Load OASIS payment SDK
  useEffect(() => {
    if (document.getElementById('mp-sdk-script')) {
      if (window.MercadoPago) initMP();
      return;
    }
    const script = document.createElement('script');
    script.id = 'mp-sdk-script';
    script.src = 'https://sdk.mercadopago.com/js/v2';
    script.async = true;
    script.onload = () => initMP();
    document.head.appendChild(script);
  }, []);

  const initMP = useCallback(async () => {
    try {
      const { data } = await api.get('/config/mp-public-key');
      if (data.public_key && window.MercadoPago) {
        mpRef.current = new window.MercadoPago(data.public_key);
        setMpReady(true);
      }
    } catch (err) {
      console.warn('MP SDK init failed:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const loadData = () => {
    api.get('/credits/packages').then(r => { setPackages(r.data.packages); }).catch(() => {});
    api.get('/credits/history').then(r => setHistory(r.data.transactions)).catch(() => {});
    api.get('/credits/my-requests').then(r => setMyRequests(r.data.requests)).catch(() => {});
    api.get('/credits/oasis-balance').then(r => setOasisBalance(r.data)).catch(() => {});
  };

  const openPurchase = (i) => {
    setSelectedPackage(i);
    setStep('select-method');
    setPaymentMethod(null);
    setResultMessage('');
    setPixCopied(false);
    setCreditRequest(null);
  };

  const close = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setStep(null);
    setSelectedPackage(null);
    setPaymentMethod(null);
    setCreditRequest(null);
    loadData();
    checkAuth();
  };

  const startPolling = (reqId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/credits/check-payment/${reqId}`);
        if (data.payment_confirmed) {
          clearInterval(pollingRef.current);
          setStep('success');
          setResultMessage(`R$ ${data.credits_added} em créditos adicionados!`);
          checkAuth();
          loadData();
          toast.success('Pagamento confirmado! Créditos adicionados.');
        }
      } catch { /* silent */ }
    }, 8000);
  };

  const tokenizeCard = async () => {
    if (!mpRef.current) throw new Error('SDK de pagamento não carregado');
    const { number, name, expMonth, expYear, cvv, cpf } = cardForm;
    if (!number || !name || !expMonth || !expYear || !cvv) throw new Error('Preencha todos os campos do cartão');

    const bin = number.replace(/\s/g, '').substring(0, 6);
    let paymentMethodId = 'visa';
    try {
      const methods = await mpRef.current.getPaymentMethods({ bin });
      if (methods.results?.length > 0) paymentMethodId = methods.results[0].id;
    } catch { /* use default */ }

    const tokenResult = await mpRef.current.createCardToken({
      cardNumber: number.replace(/\s/g, ''),
      cardholderName: name,
      cardExpirationMonth: expMonth,
      cardExpirationYear: expYear.length === 2 ? `20${expYear}` : expYear,
      securityCode: cvv,
      identificationType: 'CPF',
      identificationNumber: cpf.replace(/\D/g, ''),
    });

    return { token: tokenResult.id, paymentMethodId };
  };

  const handlePay = async () => {
    if (!paymentMethod) return;
    setSubmitting(true);
    try {
      const payload = {
        package_index: selectedPackage,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      };

      if (paymentMethod === 'card') {
        if (mpReady && cardForm.number) {
          try {
            const { token, paymentMethodId } = await tokenizeCard();
            payload.card_token = token;
            payload.card_payment_method_id = paymentMethodId;
            payload.card_payer_cpf = cardForm.cpf.replace(/\D/g, '');
          } catch (tokenErr) {
            console.warn('Tokenization failed, using Stripe fallback:', tokenErr);
            toast.info('Processando via método alternativo...');
          }
        }
      }

      if (paymentMethod === 'boleto') {
        payload.payer_first_name = boletoForm.first_name;
        payload.payer_last_name = boletoForm.last_name;
        payload.payer_cpf = boletoForm.cpf.replace(/\D/g, '');
        payload.payer_zip_code = boletoForm.zip_code.replace(/\D/g, '');
        payload.payer_street = boletoForm.street;
        payload.payer_number = boletoForm.number;
        payload.payer_neighborhood = boletoForm.neighborhood;
        payload.payer_city = boletoForm.city;
        payload.payer_state = boletoForm.state;
      }

      const { data } = await api.post('/credits/request', payload);

      if (data.instant) {
        setStep('success');
        setResultMessage(data.message);
        checkAuth();
        loadData();
        toast.success(data.message);
      } else {
        setCreditRequest(data.request);
        setStep('payment');
        toast.success(data.message);

        // Open Stripe checkout only if fallback
        if (paymentMethod === 'card' && data.request.card_fallback === 'stripe' && data.request.oasis_checkout_url) {
          window.open(data.request.oasis_checkout_url, '_blank');
        }

        if (data.request._id) {
          startPolling(data.request._id);
        }
      }
    } catch (err) {
      setResultMessage(err.response?.data?.detail || 'Erro ao processar pagamento');
      setStep('error');
      toast.error(err.response?.data?.detail || 'Erro');
    } finally {
      setSubmitting(false);
    }
  };

  const checkPaymentManually = async () => {
    if (!creditRequest?._id) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/credits/check-payment/${creditRequest._id}`);
      if (data.payment_confirmed) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        setStep('success');
        setResultMessage(`R$ ${data.credits_added} em créditos adicionados!`);
        checkAuth();
        loadData();
        toast.success('Pagamento confirmado!');
      } else {
        toast.info('Pagamento ainda não confirmado. Aguarde...');
      }
    } catch { toast.error('Erro ao verificar'); }
    finally { setChecking(false); }
  };

  const copyPix = () => {
    const code = creditRequest?.oasis_qr_code;
    if (code) {
      navigator.clipboard.writeText(code);
      setPixCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setPixCopied(false), 3000);
    }
  };

  const pkg = selectedPackage !== null ? packages[selectedPackage] : null;

  const statusBadge = {
    pending: { label: 'Aguardando', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    approved: { label: 'Aprovado', color: 'bg-green-50 text-green-700 border-green-200' },
    rejected: { label: 'Rejeitado', color: 'bg-red-50 text-red-700 border-red-200' },
  };
  const methodLabel = { pix: 'PIX', card: 'Cartão', boleto: 'Boleto', crypto: 'Cripto' };
  const historyTypeLabel = { purchase: 'Compra', purchase_fee: 'Taxa compra', sale_fee: 'Taxa venda', promotion: 'Destaque' };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="credits-page">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>Meu Saldo</h1>
        <p className="text-sm text-muted-foreground mt-2">1 crédito = R$ 1,00. Adicione saldo via PIX, Cartão, Boleto ou Cripto.</p>
        <div className="inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-full bg-primary/10 border border-primary/20">
          <Coins className="h-5 w-5 text-primary" />
          <span className="font-bold text-primary text-xl" data-testid="credit-balance">R$ {(user?.credits || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          <span className="text-sm text-muted-foreground">disponíveis</span>
        </div>
      </div>

      {/* Security Banner */}
      <div className="mb-8 p-5 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-transparent to-blue-50" data-testid="payment-info-banner">
        <div className="flex gap-4 items-start">
          <div className="p-2.5 rounded-xl bg-primary/10 shrink-0"><Shield className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-semibold text-sm mb-1.5" style={{ fontFamily: 'Outfit' }}>Pagamentos seguros via OASIS</h3>
            <div className="grid sm:grid-cols-4 gap-3 mt-3">
              {[
                { icon: QrCode, color: 'text-teal-600', title: 'PIX', desc: 'OASIS - Instantâneo' },
                { icon: CreditCard, color: 'text-blue-600', title: 'Cartão', desc: 'OASIS - Crédito/Débito' },
                { icon: FileText, color: 'text-orange-600', title: 'Boleto', desc: 'OASIS - 3 dias' },
                { icon: Bitcoin, color: 'text-amber-500', title: 'Cripto', desc: 'OASIS - Débito instantâneo' },
              ].map(m => (
                <div key={m.title} className="flex items-start gap-2 p-2.5 rounded-xl bg-white/70 border border-border">
                  <m.icon className={`h-4 w-4 ${m.color} mt-0.5 shrink-0`} />
                  <div>
                    <p className="text-xs font-semibold">{m.title}</p>
                    <p className="text-[10px] text-muted-foreground">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Packages */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {packages.map((p, i) => {
          const Icon = PACKAGE_ICONS[i] || Coins;
          return (
            <div key={i} className={`rounded-xl border-2 p-6 text-center transition-all cursor-pointer relative ${PACKAGE_COLORS[i]}`}
              onClick={() => openPurchase(i)} data-testid={`credit-package-${i}`}>
              <Icon className="h-8 w-8 mx-auto mb-3 text-foreground/70" />
              <h3 className="font-semibold text-lg mb-1" style={{ fontFamily: 'Outfit' }}>{p.name}</h3>
              <p className="text-3xl font-bold text-primary mb-1" style={{ fontFamily: 'Outfit' }}>R$ {p.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-muted-foreground mb-4">{p.credits} créditos</p>
              <Button className="w-full rounded-lg gap-2" data-testid={`buy-package-${i}`}>
                <Wallet className="h-4 w-4" /> Adicionar
              </Button>
            </div>
          );
        })}
      </div>

      {/* Purchase Dialog */}
      <Dialog open={step !== null} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className="sm:max-w-md">
          {/* Step: Select Method */}
          {step === 'select-method' && pkg && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Como deseja pagar?</DialogTitle>
                <DialogDescription>
                  Adicionar <strong>R$ {pkg?.credits.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> ao seu saldo
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2.5 py-4">
                {[
                  { id: 'pix', icon: QrCode, label: 'PIX', desc: 'OASIS - Pagamento instantâneo', color: 'green', iconColor: 'text-teal-600' },
                  { id: 'card', icon: CreditCard, label: 'Cartão de Crédito/Débito', desc: 'OASIS - Pague com cartão', color: 'blue', iconColor: 'text-blue-600' },
                  { id: 'boleto', icon: FileText, label: 'Boleto Bancário', desc: 'OASIS - Até 3 dias úteis', color: 'orange', iconColor: 'text-orange-600' },
                  { id: 'crypto', icon: Bitcoin, label: 'Saldo OASIS (Cripto)', desc: oasisBalance ? `Saldo: R$ ${oasisBalance.brl_balance.toFixed(2)}` : 'Débito do saldo OASIS', color: 'blue', iconColor: 'text-amber-500' },
                ].map(m => (
                  <button key={m.id} onClick={() => { setPaymentMethod(m.id); setStep(m.id === 'boleto' ? 'boleto-form' : m.id === 'card' ? 'card-form' : 'confirm'); }}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 border-border hover:border-${m.color}-400 hover:bg-${m.color}-50/50 transition-all text-left group`}
                    data-testid={`payment-method-${m.id}`}>
                    <div className={`p-2.5 rounded-xl bg-${m.color}-100/50 group-hover:bg-${m.color}-100 transition-colors`}>
                      <m.icon className={`h-5 w-5 ${m.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{m.label}</p>
                      <p className="text-xs text-muted-foreground">{m.desc}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step: Boleto Form */}
          {step === 'boleto-form' && pkg && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Dados para Boleto</DialogTitle>
                <DialogDescription>Valor: <strong>R$ {pkg.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></DialogDescription>
              </DialogHeader>
              <div className="py-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nome" value={boletoForm.first_name} onChange={e => setBoletoForm(f => ({...f, first_name: e.target.value}))} data-testid="boleto-first-name" />
                  <Input placeholder="Sobrenome" value={boletoForm.last_name} onChange={e => setBoletoForm(f => ({...f, last_name: e.target.value}))} data-testid="boleto-last-name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="CPF" value={boletoForm.cpf} onChange={e => setBoletoForm(f => ({...f, cpf: e.target.value}))} data-testid="boleto-cpf" />
                  <Input placeholder="CEP" value={boletoForm.zip_code} onChange={e => setBoletoForm(f => ({...f, zip_code: e.target.value}))} data-testid="boleto-zip" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input className="col-span-2" placeholder="Rua" value={boletoForm.street} onChange={e => setBoletoForm(f => ({...f, street: e.target.value}))} />
                  <Input placeholder="Nº" value={boletoForm.number} onChange={e => setBoletoForm(f => ({...f, number: e.target.value}))} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Bairro" value={boletoForm.neighborhood} onChange={e => setBoletoForm(f => ({...f, neighborhood: e.target.value}))} />
                  <Input placeholder="Cidade" value={boletoForm.city} onChange={e => setBoletoForm(f => ({...f, city: e.target.value}))} />
                  <Input placeholder="UF" maxLength={2} value={boletoForm.state} onChange={e => setBoletoForm(f => ({...f, state: e.target.value.toUpperCase()}))} />
                </div>
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setStep('select-method')}>Voltar</Button>
                <Button onClick={() => { setStep('confirm'); }} className="gap-2">Continuar</Button>
              </DialogFooter>
            </>
          )}

          {/* Step: Card Form */}
          {step === 'card-form' && pkg && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Dados do Cartão</DialogTitle>
                <DialogDescription>Valor: <strong>R$ {pkg.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> via OASIS</DialogDescription>
              </DialogHeader>
              <div className="py-3 space-y-3">
                <Input placeholder="Número do cartão" value={cardForm.number}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').substring(0, 16);
                    const formatted = v.replace(/(\d{4})(?=\d)/g, '$1 ');
                    setCardForm(f => ({ ...f, number: formatted }));
                  }}
                  data-testid="card-number" />
                <Input placeholder="Nome impresso no cartão" value={cardForm.name}
                  onChange={e => setCardForm(f => ({ ...f, name: e.target.value.toUpperCase() }))}
                  data-testid="card-name" />
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Mês (MM)" maxLength={2} value={cardForm.expMonth}
                    onChange={e => setCardForm(f => ({ ...f, expMonth: e.target.value.replace(/\D/g, '').substring(0, 2) }))}
                    data-testid="card-exp-month" />
                  <Input placeholder="Ano (AA)" maxLength={2} value={cardForm.expYear}
                    onChange={e => setCardForm(f => ({ ...f, expYear: e.target.value.replace(/\D/g, '').substring(0, 2) }))}
                    data-testid="card-exp-year" />
                  <Input placeholder="CVV" maxLength={4} type="password" value={cardForm.cvv}
                    onChange={e => setCardForm(f => ({ ...f, cvv: e.target.value.replace(/\D/g, '').substring(0, 4) }))}
                    data-testid="card-cvv" />
                </div>
                <Input placeholder="CPF do titular" value={cardForm.cpf}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').substring(0, 11);
                    let formatted = v;
                    if (v.length > 9) formatted = `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6,9)}-${v.slice(9)}`;
                    else if (v.length > 6) formatted = `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6)}`;
                    else if (v.length > 3) formatted = `${v.slice(0,3)}.${v.slice(3)}`;
                    setCardForm(f => ({ ...f, cpf: formatted }));
                  }}
                  data-testid="card-cpf" />
                {!mpReady && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Se o pagamento via OASIS falhar, será redirecionado para o Stripe.
                  </p>
                )}
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setStep('select-method')}>Voltar</Button>
                <Button onClick={() => setStep('confirm')} className="gap-2">Continuar</Button>
              </DialogFooter>
            </>
          )}

          {/* Step: Confirm */}
          {step === 'confirm' && pkg && paymentMethod && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Confirmar compra</DialogTitle>
                <DialogDescription>
                  {paymentMethod === 'pix' ? 'PIX via OASIS' :
                   paymentMethod === 'card' ? 'Cartão via OASIS' :
                   paymentMethod === 'boleto' ? 'Boleto Bancário' : 'Débito OASIS (Cripto)'}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="rounded-xl border border-border p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Pacote</span>
                    <span className="font-medium">{pkg.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Créditos</span>
                    <span className="font-medium">R$ {pkg.credits.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-border pt-3 flex justify-between font-bold">
                    <span>Valor a pagar</span>
                    <span className="text-primary text-lg">R$ {pkg.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {paymentMethod === 'crypto' && oasisBalance && oasisBalance.brl_balance < pkg.price && (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-center">
                    <p className="text-sm text-destructive font-medium">Saldo insuficiente na OASIS</p>
                    <a href={OASIS_DEPOSIT_URL} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1">
                      Depositar na OASIS <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setStep('select-method')}>Voltar</Button>
                <Button onClick={handlePay} disabled={submitting || (paymentMethod === 'crypto' && oasisBalance && oasisBalance.brl_balance < pkg.price)} className="gap-2" data-testid="confirm-purchase-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {paymentMethod === 'pix' ? 'Gerar PIX' :
                   paymentMethod === 'card' ? 'Pagar com Cartão' :
                   paymentMethod === 'boleto' ? 'Gerar Boleto' : 'Confirmar pagamento'}
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Step: Payment created — show QR/redirect/boleto */}
          {step === 'payment' && creditRequest && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>
                  {paymentMethod === 'pix' ? 'Pagamento PIX' :
                   paymentMethod === 'card' ? (creditRequest?.card_fallback === 'stripe' ? 'Pagamento com Cartão (Stripe)' : 'Pagamento com Cartão') :
                   'Boleto Gerado'}
                </DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-4">
                {/* PIX QR Code */}
                {paymentMethod === 'pix' && (
                  <>
                    {creditRequest.oasis_qr_code_base64 && (
                      <div className="flex justify-center">
                        <img src={`data:image/png;base64,${creditRequest.oasis_qr_code_base64}`} alt="QR Code" className="w-52 h-52 rounded-xl border border-border" data-testid="pix-qr-code" />
                      </div>
                    )}
                    {creditRequest.oasis_qr_code && (
                      <div className="flex items-center gap-2 bg-muted rounded-xl p-3">
                        <code className="flex-1 text-xs break-all">{creditRequest.oasis_qr_code.substring(0, 60)}...</code>
                        <Button variant="outline" size="sm" onClick={copyPix} className="shrink-0 gap-1" data-testid="copy-pix-btn">
                          {pixCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          {pixCopied ? 'Copiado!' : 'Copiar'}
                        </Button>
                      </div>
                    )}
                    {creditRequest.oasis_ticket_url && (
                      <a href={creditRequest.oasis_ticket_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline justify-center w-full">
                        <ExternalLink className="h-3.5 w-3.5" /> Abrir no OASIS
                      </a>
                    )}
                  </>
                )}

                {/* Card — MP or Stripe fallback */}
                {paymentMethod === 'card' && creditRequest.card_fallback === 'stripe' && (
                  <div className="text-center space-y-3">
                    <CreditCard className="h-12 w-12 text-blue-500 mx-auto" />
                    <p className="text-sm font-medium">Complete o pagamento no Stripe</p>
                    <p className="text-xs text-muted-foreground">Uma janela foi aberta. Após pagar, clique em &ldquo;Verificar&rdquo;.</p>
                    {creditRequest.oasis_checkout_url && (
                      <Button variant="outline" size="sm" className="gap-1.5"
                        onClick={() => window.open(creditRequest.oasis_checkout_url, '_blank')}>
                        <ExternalLink className="h-3 w-3" /> Reabrir página
                      </Button>
                    )}
                  </div>
                )}

                {paymentMethod === 'card' && creditRequest.card_fallback !== 'stripe' && (
                  <div className="text-center space-y-3">
                    <CreditCard className="h-12 w-12 text-blue-500 mx-auto" />
                    <p className="text-sm font-medium">Processando pagamento...</p>
                    <p className="text-xs text-muted-foreground">Seu pagamento está sendo processado pela OASIS.</p>
                  </div>
                )}

                {/* Boleto */}
                {paymentMethod === 'boleto' && (
                  <div className="text-center space-y-3">
                    <FileText className="h-12 w-12 text-orange-500 mx-auto" />
                    <p className="text-sm font-medium">Boleto gerado!</p>
                    <p className="text-xs text-muted-foreground">Pague em banco, lotérica ou app bancário. Prazo: até 3 dias úteis.</p>
                    {(creditRequest.boleto_url || creditRequest.oasis_ticket_url) && (
                      <Button variant="outline" size="sm" className="gap-1.5"
                        onClick={() => window.open(creditRequest.boleto_url || creditRequest.oasis_ticket_url, '_blank')}>
                        <ExternalLink className="h-3 w-3" /> Abrir Boleto
                      </Button>
                    )}
                  </div>
                )}

                <div className="text-sm text-center text-muted-foreground">
                  Valor: <strong className="text-foreground">R$ {pkg?.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                </div>

                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-800 text-center">
                    Após a confirmação do pagamento, seus créditos serão adicionados automaticamente.
                  </p>
                </div>
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={close}>Fechar</Button>
                <Button variant="outline" onClick={checkPaymentManually} disabled={checking} className="gap-1.5" data-testid="check-payment-btn">
                  {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Verificar pagamento
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Success */}
          {step === 'success' && (
            <>
              <div className="py-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Créditos adicionados!</h3>
                <p className="text-sm text-muted-foreground">{resultMessage}</p>
              </div>
              <DialogFooter><Button onClick={close} className="w-full" data-testid="success-close-btn">Entendido</Button></DialogFooter>
            </>
          )}

          {/* Error */}
          {step === 'error' && (
            <>
              <div className="py-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>Erro</h3>
                <p className="text-sm text-muted-foreground">{resultMessage}</p>
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={close}>Fechar</Button>
                <Button onClick={() => setStep('select-method')}>Tentar novamente</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pending Requests */}
      {myRequests.filter(r => r.status === 'pending').length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Clock className="h-5 w-5 text-amber-500" /> Solicitações Pendentes
          </h2>
          <div className="space-y-2">
            {myRequests.filter(r => r.status === 'pending').map(r => (
              <div key={r._id} className="flex items-center justify-between p-4 rounded-xl border border-amber-200 bg-amber-50/50">
                <div>
                  <p className="font-medium text-sm">{r.package_name} - R$ {Number(r.credits).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground">
                    R$ {r.amount_brl?.toFixed(2).replace('.', ',')} via {methodLabel[r.payment_method] || r.payment_method} - {new Date(r.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
                <Badge className={statusBadge.pending.color}>{statusBadge.pending.label}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      {myRequests.filter(r => r.status !== 'pending').length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Histórico</h2>
          <div className="space-y-2">
            {myRequests.filter(r => r.status !== 'pending').slice(0, 10).map(r => {
              const s = statusBadge[r.status] || statusBadge.pending;
              return (
                <div key={r._id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card text-sm">
                  <div>
                    <p className="font-medium">{r.package_name} - R$ {Number(r.credits).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-muted-foreground">{methodLabel[r.payment_method] || r.payment_method} - {new Date(r.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <Badge className={s.color}>{s.label}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Credit History */}
      {history.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Movimentação de Créditos</h2>
          <div className="space-y-2">
            {history.slice(0, 20).map((tx, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card text-sm">
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded-lg ${tx.credits > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    {tx.credits > 0 ? <Coins className="h-4 w-4 text-green-600" /> : <ArrowRight className="h-4 w-4 text-red-500" />}
                  </div>
                  <div>
                    <p className="font-medium">{tx.package_name || historyTypeLabel[tx.type] || tx.type}</p>
                    <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
                <span className={`font-semibold ${tx.credits > 0 ? 'text-green-600' : 'text-destructive'}`}>
                  {tx.credits > 0 ? '+' : ''}{tx.credits}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const OASIS_DEPOSIT_URL = "https://crypto-hub-rebuild.emergent.host";
